import { useCallback } from "react";
import useSWR from "swr";
import { DotNestedKeys, NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Workflow } from "./types";

export const useMyCarWorkflowsApi = () => {
  const api = useApi();

  const useWorkflows = useCallback(
    <K extends DotNestedKeys<Workflow>>(
      query: PaginationQueryWithFields<NestedPick<Workflow, K>>,
      signal: AbortSignal,
      carId: string,
    ) => {
      const url = buildUrl<NestedPick<Workflow, K>>(`/my_cars/${carId}/workflows`, query);

      return useSWR<Paginated<NestedPick<Workflow, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<Workflow, K>>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    useWorkflows,
  };
};

export const useAdminWorkflowsApi = () => {
  const api = useApi();

  const createWorkflow = useCallback(
    async (carTaskId: string, carId: string): Promise<{ id: string }> => {
      const url = `/admin/car_tasks/${carTaskId}/notify_driver`;
      const id = await api.post<{ id: string }, never>(url);
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
      return id;
    },
    [api],
  );

  const notifyDriver = useCallback(
    async (workflowId: string, carId: string) => {
      const url = `/admin/workflows/${workflowId}/notify_driver`;
      await api.put(url, undefined);
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  const rejectImages = useCallback(
    async (workflowId: string, emailText: string, carId: string) => {
      const url = `/admin/workflows/${workflowId}/reject`;
      await api.put<{ emailText: string }>(url, { emailText });
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  const approveImages = useCallback(
    async (workflowId: string, carId: string) => {
      const url = `/admin/workflows/${workflowId}/approve`;
      await api.put(url, null);
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  const useWorkflows = useCallback(
    <K extends DotNestedKeys<Workflow>>(
      query: PaginationQueryWithFields<NestedPick<Workflow, K>>,
      signal: AbortSignal,
      carId: string,
    ) => {
      const url = buildUrl<NestedPick<Workflow, K>>(
        carId ? `/admin/cars/${carId}/workflows` : "/admin/workflows",
        query,
      );

      return useSWR<Paginated<NestedPick<Workflow, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<Workflow, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const getWorkflow = useCallback(
    (workflowId: string, signal: AbortSignal) => {
      const url = `/admin/workflows/${workflowId}`;
      return useSWR<Workflow | null, CustomError, string>(url, (url) => api.get<Workflow>({ url, signal }));
    },
    [api],
  );

  const cancelWorkflow = useCallback(
    async (workflowId: string, carId: string) => {
      const url = `/admin/workflows/${workflowId}/cancel`;
      await api.put(url, null);
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  const reopenWorkflow = useCallback(
    async (workflowId: string, carId: string) => {
      const url = `/admin/workflows/${workflowId}/reopen`;
      await api.put(url, null);
      api.mutateCache([
        url,
        `/admin/cars/${carId}/overview`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
        `/admin/cars/${carId}/tasks`,
        `/admin/car_tasks`,
      ]);
    },
    [api],
  );

  return {
    createWorkflow,
    notifyDriver,
    useWorkflows,
    rejectImages,
    approveImages,
    getWorkflow,
    cancelWorkflow,
    reopenWorkflow,
  };
};
